/* eslint-disable react/display-name */
import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Text, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import BlockContent from '@sanity/block-content-to-react';
import { BoxContent, BoxHeader, BoxMain } from 'components';
import { loadFaqs } from 'modules/faq/faqSlice';
import { rgba } from 'polished';
import React, { FC, useEffect, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { rem } from 'utils/fonts';

interface Props {
  size?: 'full' | 'lg';
  page: string;
  partnerPage?: string;
  animate?: boolean;
}

const StyledAccordion = styled(Accordion)`
  margin-top: 30px;
  margin-bottom: -10px;
`;

const Header = styled(AccordionButton)`
  display: flex;
  justify-content: space-between;
  font-weight: 500;
  font-size: ${rem(18)};
  padding: 20px 10px;
  border-radius: 9px;
`;

const Question = styled(Text)`
  font-size: ${rem('18px')};
  font-weight: 500;
  text-align: left;
`;

const Item = styled(AccordionItem)`
  margin: 0 -10px;

  :first-of-type {
    border-top-width: 0;
  }

  :last-of-type {
    border-bottom-width: 0;
  }
`;

interface MarksProps {
  children: ReactElement;
  mark: {
    href: string;
  };
}

const serializers = {
  marks: {
    link: (props: MarksProps) => {
      return (
        <a href={props.mark.href} target="_blank" rel="noreferrer noopener">
          {props.children}
        </a>
      );
    },
  },
};

const Faq: FC<Props> = ({ size = 'full', page, partnerPage, animate }) => {
  const { colors } = useTheme();
  const { t, ready } = useTranslation('common');
  const { loading, items } = useSelector((state: RootState) => state.faqs);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadFaqs(page, partnerPage));
  }, [page, partnerPage]);

  return (
    <BoxContent boxWidth={size} animate={animate} id="faq-module" className="faq-module">
      <BoxMain ready={!loading && ready} overlayHeight="320px" overlayType="list">
        <BoxHeader title={t('faq.title')} linkText="" />
        <StyledAccordion allowMultiple>
          {items.map(f => (
            <Item key={f.question}>
              <Header data-testid="faq-btn" color="gray.400" _hover={{ bg: rgba(colors.pink[300], 0.5) }}>
                <Question color="gray.900">{f.question}</Question>
                <AccordionIcon />
              </Header>
              <AccordionPanel pb={4} sx={{ a: { color: 'pink.500' } }}>
                <BlockContent blocks={f.answer} serializers={serializers} />
              </AccordionPanel>
            </Item>
          ))}
        </StyledAccordion>
      </BoxMain>
    </BoxContent>
  );
};

export default Faq;
